<template>
  <div
    class="bg-cover bg-top flex-none flex flex-col rounded-bottom h-132 items-center justify-center relative overflow-hidden"
    :class="
      cardImg
        ? planBgClass(`${cardImg}`)
        : planBgClass(
            plan?.RegularDataAllowance
              ? `${plan?.RegularDataAllowance}`
              : `${plan?.DataAllowance}`,
          )
    "
  >
    <div v-if="cardIcons" class="flex flex-row">
      <nuxt-icon
        v-for="cardIcon in cardIcons"
        :key="cardIcon"
        class="text-[5.6rem]"
        :name="cardIcon"
        aria-hidden="true"
        style="color: black"
      />
    </div>

    <span
      v-if="!cardIcons"
      aria-hidden="true"
      class="heading-plan-card text-text-on-highlight mt-8 z-10"
    >
      {{ plan?.DataAllowance }}
    </span>

    <div
      v-if="bundle?.Labels?.at(0)?.Text"
      aria-hidden="true"
      class="absolute top-0 left-0"
    >
      <AppLabel
        :color="bundle?.Labels?.at(0)?.Color"
        :text="bundle?.Labels?.at(0)?.Text"
        line-height="leading-none"
      />
    </div>

    <p
      v-if="hasExtraAllowance"
      aria-hidden="true"
      class="heading-xs line-through z-10"
    >
      {{ plan?.RegularDataAllowance }}
    </p>
  </div>

  <div class="p-12 flex flex-col justify-between h-full">
    <div class="flex flex-row gap-4 pt-4">
      <PostPaidCardcomponentPlanHeading
        :data-allowance="plan?.DataAllowance"
        :regular-data-allowance="plan?.RegularDataAllowance"
        :product-name="props.product?.ProductName"
        :has-extra-allowance="hasExtraAllowance"
        :override-title="plan?.IsFamilyPlan ? 'Familj' : undefined"
      />
    </div>

    <PostPaidCardcomponentCampaignLabelTextForSR :bundle="bundle" />

    <div class="min-h-48 h-full flex flex-col justify-start">
      <MarkupGenerator
        v-if="bodyMarkup !== null"
        :is-page="false"
        :page-data="bodyMarkup as any"
      />
    </div>

    <div class="flex flex-col pb-16">
      <PostPaidCardcomponentPriceTexts :plan="plan" />
    </div>
    <div aria-hidden="true" :class="btnClasses">
      {{ buttonText || 'Beställ' }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { ListingProduct } from '~/models/ListingProduct.api.model';
import type { MarkupData } from '~/repository/modules/category.module';
import { planBgClass } from '~/utils';

const btnClasses = computed(
  () => `
  hyphens-auto
  p-9
  gap-16
  cursor-pointer
  flex
  justify-center
  items-center
  hover:bg-surface-action-hover
  active:bg-border-secondary
  border-2
  border-solid
  border-border-primary
  text-text-primary
  rounded
  shadow-button
  text-18
  leading-[2.16rem]
  disabled:bg-white-500
  disabled:text-grey-200
  disabled:border-grey-200
  disabled:shadow-none
  no-underline
  w-full
  bg-white-50
  dark:bg-surface-action-2
  dark:hover:bg-surface-action-hover
`,
);

const props = defineProps<{
  product: ListingProduct;

  buttonText?: string;
  cardImg?: string;
  cardIcons?: string[];
}>();

const bundle = props.product?.Variants?.at(0)?.Prices.BundlePrices?.at(0);
const plan = bundle?.Plan;

const hasExtraAllowance = computed(
  () =>
    !!(
      plan?.RegularDataAllowance &&
      plan?.DataAllowance !== plan?.RegularDataAllowance
    ),
);

const bodyMarkup = ref<MarkupData | null>(null);
if (props.product?.ShortDescription) {
  bodyMarkup.value = {
    PageContent: props.product.ShortDescription,
  };
}

if (bodyMarkup.value) {
  const bulletList = bodyMarkup.value.PageContent.find(
    (x) => x.Component.toLowerCase() === 'simplebulletlist',
  );

  if (bulletList) {
    const bindperiodBullet = bundle?.BindingPeriod?.Months + ' mån bindning';

    if (
      !bulletList.Items.some(
        (item: { Text: string }) => item.Text == bindperiodBullet,
      ) &&
      bundle?.BindingPeriod?.Months != 0
    ) {
      bulletList.Items.unshift({
        Text: bindperiodBullet,
      });
    }

    if (
      bundle?.Plan?.OfferValidityInMonthsDiscount ||
      bundle?.Plan?.OfferValidityInMonthsDataAmount ||
      bundle?.Plan?.OfferValidityInMonthsFreePeriod
    ) {
      const offerValidityInMonthsValue =
        bundle?.Plan?.OfferValidityInMonthsDiscount ||
        bundle?.Plan?.OfferValidityInMonthsDataAmount ||
        bundle?.Plan?.OfferValidityInMonthsFreePeriod;

      const offerValidityInMonthsBullet =
        'Rabatten gäller i ' + offerValidityInMonthsValue + ' månader';

      if (
        !bulletList.Items.some(
          (item: { Text: string }) => item.Text == offerValidityInMonthsBullet,
        )
      ) {
        bulletList.Items.unshift({
          Text: offerValidityInMonthsBullet,
        });
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.rounded-bottom {
  clip-path: ellipse(93% 80% at 49% 17%);
}
</style>
